import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ZfBox, ZfDivider, ZfLoadingSpinner, ZfStack, ZfTypography } from '@zafire/react';
import { format } from 'date-fns';
import ContactNote from 'types/ContactNote';
import { AuditStatus, auditStatusLabels } from 'views/ContactNoteForm/components/data/auditHistoryStatus';
import { useGetAuditHistoryStatus } from 'hooks/useGetAuditHitoryStatus';
import { GlobalState } from 'reducers/stateTypes';
import { AgGridReact } from 'ag-grid-react';
import { themeZafire } from '@zafire/ag-grid-enterprise';
import { ColDef, GetDetailRowDataParams, ITooltipParams } from 'ag-grid-community';
import { submissionStatusPlaceholder } from 'views/ContactNoteFormDialog/zafire/components/data/ContactNoteDialogStatus';
import { Box } from '@mui/material';

import AuditHistoryCustomCell from './AuditHistoryCustomCell';
import AuditHistoryCustomCellDialog from './AuditHistoryCustomCellDialog';

export type RevisionDetail = {
  attribute: string;
  previousValue: string;
  newValue: string;
};

export const REVISION_FILTERED_LIST = ['updateDateTime', 'actions', 'isTimeAllowed', 'updatedBy'];

export type RevisionInputType =
  | 'contactType'
  | 'additionalAttendees'
  | 'topics'
  | 'locationCountry'
  | 'locationCity'
  | 'qualifiedClientContact'
  | 'initiatedBy'
  | 'crossBorderActivity'
  | 'updateDateTime'
  | 'createdDateTime'
  | 'submittedDateTime'
  | 'startDateTime'
  | 'status'
  | 'endDateTime';

export type AuditHistoryResponse = {
  id: string;
  contactNoteId: string;
  status: string;
  actions: string;
  updatedBy: string;
  updateDateTime: Date;
  revistionNumber: number;
  revisionHistory?: RevisionDetail[];
};

type RowData = {
  action: string;
  actionBy: string;
  dateTime: string;
  children?: RevisionDetail[];
};

const ContactNoteAuditHistory = ({ contactNote }: { contactNote: ContactNote }) => {
  const { t } = useTranslation();
  const employeeOptions = useSelector((state: GlobalState) => state.resources.employeeOptions);
  const initiatedByOptions = useSelector((state: GlobalState) => state.resources.initiatedByOptions);
  const topicOptions = useSelector((state: GlobalState) => state.resources.topicsOptions);
  const contactTypeOptions = useSelector((state: GlobalState) => state.resources.contactTypeOptions);
  const countryOptions = useSelector((state: GlobalState) => state.resources.countries);
  const [showCellData, setShowCellData] = useState({ header: '', title: '', data: '' });
  const [showCellDialog, setShowCellDialog] = useState(false);

  const { data: auditHistory, isSuccess } = useGetAuditHistoryStatus(contactNote.id);

  const ContactNoteAuditHistoryColumn: ColDef<RowData>[] = useMemo(
    () => [
      {
        field: 'dateTime',
        headerName: t('contactNote.auditHistory.date'),
        flex: 1,
        cellRenderer: 'agGroupCellRenderer',
        tooltipValueGetter: (params: ITooltipParams) => params.value,
      },
      {
        field: 'actionBy',
        headerName: t('contactNote.auditHistory.user'),
        flex: 1,
        tooltipValueGetter: (params: ITooltipParams) => params.value,
      },
      {
        field: 'action',
        headerName: t('contactNote.auditHistory.action'),
        flex: 1,
        tooltipValueGetter: (params: ITooltipParams) => params.value,
      },
    ],
    [t],
  );
  const getRevisionLabel = useCallback(
    <T extends RevisionInputType>(attribute: T, value: string | string[] | boolean) => {
      switch (attribute) {
        case 'initiatedBy': {
          return initiatedByOptions.find((option) => option.value === value)?.label || value;
        }
        case 'topics': {
          if (Array.isArray(value)) {
            return value.map((v) => topicOptions.find((option) => option.value === v)?.label || v).join(', ');
          } else if (typeof value === 'string' && value.includes(',')) {
            return value
              .split(',')
              .map((v) => topicOptions.find((option) => option.value === v.trim())?.label || v)
              .join(', ');
          }
          return topicOptions.find((option) => option.value === value)?.label || value;
        }
        case 'contactType': {
          return contactTypeOptions.find((option) => option.value === value)?.label || value;
        }
        case 'locationCountry': {
          return countryOptions.find((option) => option.value === value)?.label || value;
        }
        case 'additionalAttendees': {
          if (Array.isArray(value)) {
            return value.map((v) => employeeOptions.find((option) => option.value === v)?.label || v).join(', ');
          }
          return value;
        }
        case 'qualifiedClientContact': {
          return value
            ? value === 'true' || value === true
              ? t('noteForm.contactDetails.yes', 'Yes')
              : t('noteForm.contactDetails.no', 'No')
            : '';
        }
        case 'crossBorderActivity': {
          return value
            ? value === 'true' || value === true
              ? t('noteForm.contactDetails.yes', 'Yes')
              : t('noteForm.contactDetails.no', 'No')
            : '';
        }
        case 'status': {
          return submissionStatusPlaceholder.find((status) => status.value === value)?.label || value;
        }
        case 'createdDateTime':
        case 'submittedDateTime':
        case 'startDateTime':
        case 'endDateTime': {
          if (typeof value === 'string' && value) {
            if (!value.includes('T')) {
              return format(new Date(value), 'dd.MM.yyyy');
            }
            return format(new Date(value), 'dd.MM.yyyy | hh:mm a');
          }
          return value;
        }

        default: {
          return value;
        }
      }
    },
    [initiatedByOptions, topicOptions, contactTypeOptions, countryOptions],
  );

  const auditHistoryData = useMemo(() => {
    if (auditHistory && auditHistory.length > 0) {
      const sorted = auditHistory.sort(
        (a, b) => new Date(b.updateDateTime).getTime() - new Date(a.updateDateTime).getTime(),
      );

      return sorted.map((item) => {
        const dateTime = `${format(new Date(item.updateDateTime), 'dd.MM.yyyy | hh:mm a')}`;
        const action = auditStatusLabels[item.actions as AuditStatus];
        const actionBy = employeeOptions.find((co) => co.value === item.updatedBy?.toLowerCase())?.label;
        return {
          action: action,
          actionBy:
            actionBy ||
            (item.updatedBy ? (item.updatedBy === 'SYSTEM' ? 'SYSTEM' : `N/A(${item.updatedBy.toLowerCase()})`) : ''),
          dateTime,
          children: item?.revisionHistory
            ?.filter((actionItem) => !REVISION_FILTERED_LIST.includes(actionItem.attribute))
            ?.map((rev) => ({
              attribute: t(`noteForm.contactDetails.${rev.attribute}`, { defaultValue: rev.attribute }),
              previousValue: getRevisionLabel(rev.attribute as RevisionInputType, rev.previousValue),
              newValue: getRevisionLabel(rev.attribute as RevisionInputType, rev.newValue),
            })) as RevisionDetail[],
        };
      });
    }
    return [];
  }, [auditHistory]);

  const detailCellRendererParams = useMemo(() => {
    return {
      detailGridOptions: {
        columnDefs: [
          { field: 'attribute', headerName: t('contactNote.auditHistory.attribute'), flex: 1 },
          {
            field: 'previousValue',
            headerName: t('contactNote.auditHistory.previousValue'),
            flex: 1,
            cellRenderer: AuditHistoryCustomCell,
            cellRendererParams: {
              onClick: (params: { title: string; data: string }) => {
                setShowCellData({
                  header: t('contactNote.auditHistory.previousValue'),
                  title: params.title,
                  data: params.data,
                });
                setShowCellDialog(true);
              },
            },
          },
          {
            field: 'newValue',
            headerName: t('contactNote.auditHistory.newValue'),
            flex: 1,
            cellRenderer: AuditHistoryCustomCell,
            cellRendererParams: {
              onClick: (params: { title: string; data: string }) => {
                setShowCellData({
                  header: t('contactNote.auditHistory.newValue'),
                  title: params.title,
                  data: params.data,
                });
                setShowCellDialog(true);
              },
            },
          },
        ],
        defaultColDef: { flex: 1 },
        masterDetail: true,
      },
      getDetailRowData: (params: GetDetailRowDataParams) => {
        params.successCallback(params.data.children ?? []);
      },
    };
  }, []);
  return (
    <>
      <AuditHistoryCustomCellDialog
        showDialog={showCellDialog}
        onDialogClose={() => setShowCellDialog(false)}
        data={showCellData}
      />
      <ZfBox styles={{ width: '100%' }}>
        <ZfStack spacing="extra-large" style={{ marginTop: 'var(--gap-vertical-md)' }}>
          <ZfStack direction="row" spacing="small">
            <ZfTypography variant="label-medium" color="emphasis" weight="bold">
              {t('contactNote.details.editHistory')}
            </ZfTypography>
            <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-sm)' }} />
          </ZfStack>
          <ZfStack direction="row" spacing="small">
            <ZfStack style={{ flex: 1 }}>
              <ZfTypography variant="body-medium" color="normal">
                {t('contactNote.details.noteId')}
              </ZfTypography>
              <ZfTypography variant="body-medium" color="emphasis">
                {contactNote.contactNoteId}
              </ZfTypography>
            </ZfStack>
            <ZfStack style={{ flex: 1 }}>
              <ZfTypography variant="body-medium" color="normal">
                {t('contactNote.details.creationDate')}
              </ZfTypography>
              <ZfTypography variant="body-medium" color="emphasis">
                {`${format(new Date(contactNote.createdDateTime), 'dd.MM.yyyy | hh:mm a')}`}
              </ZfTypography>
            </ZfStack>
          </ZfStack>
          {isSuccess && auditHistory ? (
            <Box
              sx={{
                '.ag-tooltip': {
                  maxWidth: '300px',
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  wordBreak: 'break-word',
                },
              }}
            >
              <ZfBox styles={{ maxHeight: '350px' }}>
                {' '}
                <ZfBox styles={{ width: '100%' }}>
                  <AgGridReact
                    domLayout="autoHeight"
                    defaultColDef={{ suppressHeaderMenuButton: true }}
                    rowData={auditHistoryData}
                    columnDefs={ContactNoteAuditHistoryColumn}
                    theme={themeZafire}
                    onRowClicked={(params) => {
                      const node = params.node;
                      node.setExpanded(!node.expanded);
                    }}
                    detailCellRendererParams={detailCellRendererParams}
                    detailRowAutoHeight={true}
                    masterDetail={true}
                    suppressCellFocus
                    isRowMaster={(data) => Array.isArray(data?.children) && data.children.length > 0}
                  />
                </ZfBox>
              </ZfBox>
            </Box>
          ) : (
            <ZfLoadingSpinner size="large" color="primary" type="infinite"></ZfLoadingSpinner>
          )}
        </ZfStack>
      </ZfBox>
    </>
  );
};

export default ContactNoteAuditHistory;
